/**
 * The published age bands, mirroring `backend/api/src/common/age-bands.ts`.
 *
 * A mirror, not a second source of truth — the same arrangement
 * `lib/nearby-radius.ts` has with the server's radius bounds. The server
 * validates every value it is sent, so a drifted client can only mislabel its
 * own slider; it can never widen or invent a range.
 *
 * The band is the granularity at which age is disclosed anywhere in the product,
 * so it is also the granularity at which age may be *asked about*. A control
 * that offered 26-28 would be promising a precision the data does not have and
 * the privacy model will not allow — and, since the server refuses a non-band
 * range, one that would silently resolve to "any age".
 */

/**
 * Every band floor. NOT a uniform five-year grid: `18-24` is seven years wide
 * because 18 is the platform minimum, and `75+` is open-ended.
 */
export const AGE_BAND_FLOORS = [
  18, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75,
] as const;

export const AGE_BAND_MIN_FLOOR = 18;
export const OPEN_AGE_BAND_FLOOR = 75;

/** The oldest age still inside a band, or null for the open top band. */
export function ageBandUpperEdge(floor: number): number | null {
  if (floor === OPEN_AGE_BAND_FLOOR) return null;
  if (floor === AGE_BAND_MIN_FLOOR) return 24;
  return floor + 4;
}

/** The nearest band floor at or below an age. */
export function ageBandFloorFor(age: number): number {
  if (age >= OPEN_AGE_BAND_FLOOR) return OPEN_AGE_BAND_FLOOR;
  if (age < 25) return AGE_BAND_MIN_FLOOR;
  return Math.floor(age / 5) * 5;
}

/**
 * Snap a raw slider position to a band floor.
 *
 * Nearest floor rather than the one below, so a thumb dragged to 29 lands on 30
 * instead of falling back to 25 — a control that always rounds down feels
 * broken at the top of every band.
 */
export function snapToBandFloor(age: number): number {
  let best = AGE_BAND_FLOORS[0] as number;
  for (const floor of AGE_BAND_FLOORS) {
    if (Math.abs(floor - age) < Math.abs(best - age)) best = floor;
  }
  return best;
}

export type AgeBandRange = { minFloor: number; maxFloor: number };

/** The whole span: every band, which is what "Any age" means. */
export function isWholeRange(range: AgeBandRange): boolean {
  return (
    range.minFloor <= AGE_BAND_MIN_FLOOR &&
    range.maxFloor >= OPEN_AGE_BAND_FLOOR
  );
}

/**
 * The label for a band range: "25-34", "50+", or null for no restriction.
 *
 * The upper number is the band's UPPER EDGE, never its floor. A control whose
 * top thumb sits on floor 30 and prints "30" while admitting 34-year-olds is
 * the display/filter disagreement the band decision exists to remove — just
 * moved to the top of the range.
 */
export function ageBandRangeLabel(range: AgeBandRange): string | null {
  if (isWholeRange(range)) return null;
  const upper = ageBandUpperEdge(range.maxFloor);
  return upper === null ? `${range.minFloor}+` : `${range.minFloor}-${upper}`;
}

/** Read a stored label back into floors. Null (or an unreadable value) is "any age". */
export function parseBandRangeLabel(
  label: string | null | undefined,
): AgeBandRange | null {
  if (!label) return null;
  const open = /^(\d+)\+$/.exec(label.trim());
  if (open) {
    const minFloor = Number(open[1]);
    return AGE_BAND_FLOORS.includes(minFloor as never)
      ? { minFloor, maxFloor: OPEN_AGE_BAND_FLOOR }
      : null;
  }
  const closed = /^(\d+)-(\d+)$/.exec(label.trim());
  if (!closed) return null;
  const minFloor = Number(closed[1]);
  const maxEdge = Number(closed[2]);
  const maxFloor = AGE_BAND_FLOORS.find(
    (floor) => ageBandUpperEdge(floor) === maxEdge,
  );
  if (!AGE_BAND_FLOORS.includes(minFloor as never) || maxFloor === undefined) {
    return null;
  }
  return minFloor <= maxFloor ? { minFloor, maxFloor } : null;
}
